import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import * as Recharts from "recharts";
import { useStore } from "../../app/stores/store";
import { useMemberStatusOptions } from "../../app/common/enums/memberStatus";
import { useGenderTypeOptions } from "../../app/common/enums/salutationType";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { useNavigate } from "react-router-dom";

const ResponsiveContainer: any = (Recharts as any).ResponsiveContainer;
const BarChart: any = (Recharts as any).BarChart;
const Bar: any = (Recharts as any).Bar;
const XAxis: any = (Recharts as any).XAxis;
const YAxis: any = (Recharts as any).YAxis;
const ReTooltip: any = (Recharts as any).Tooltip;
const PieChart: any = (Recharts as any).PieChart;
const Pie: any = (Recharts as any).Pie;
const Cell: any = (Recharts as any).Cell;
const ReLegend: any = (Recharts as any).Legend;
const LabelList: any = (Recharts as any).LabelList;

const COLORS = {
  primary: "#3AA6FF",
  membersYear: "#4BC0C0",
  spark: "#FF9F40",
  fee: ["#36A2EB", "#FFCE56", "#2afc1b"],
  status: ["#36A2EB", "#FFCE56", "#2afc1b", "#D3D3D3"],
  gender: ["#36A2EB", "#2afc1b"],
};

function formatNumber(v: number) {
  return v?.toLocaleString ? v.toLocaleString() : String(v);
}

function shouldShowBarLabel(value: number, max: number) {
  if (!value || value <= 0 || !max || max <= 0) return false;
  return value / max >= 0.05;
}

export default function Dashboard() {
  const statisticStore = useStore((s) => s.statisticStore);
  const loggedUser = useStore((s) => s.userStore.loggedUser);
  const loadStatistics = useStore((s) => s.statisticStore.loadStatistics);
  const memberStatusOptions = useMemberStatusOptions();
  const salutationTypeOptions = useGenderTypeOptions();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [breakpoint, setBreakpoint] = useState<"mobile" | "tablet" | "desktop">(() => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    return w < 600 ? "mobile" : w < 992 ? "tablet" : "desktop";
  });

  useEffect(() => {
    loadStatistics();
    const onResize = () => {
      const w = window.innerWidth;
      const bp = w < 600 ? "mobile" : w < 992 ? "tablet" : "desktop";
      setBreakpoint(bp);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [loadStatistics]);

  const chartHeight = breakpoint === "mobile" ? 220 : breakpoint === "tablet" ? 260 : 320;
  const tickFont = breakpoint === "mobile" ? 9 : 11;
  const chartFont = breakpoint === "mobile" ? 10 : breakpoint === "tablet" ? 11 : 13;
  const kpiValueFont = breakpoint === "mobile" ? 18 : breakpoint === "tablet" ? 22 : 26;
  const kpiLabelFont = breakpoint === "mobile" ? 10 : breakpoint === "tablet" ? 12 : 14;

  if (statisticStore.loading)
    return <LoadingComponent content={t("general.loading")} />;

  const membershipPaymentsData = (statisticStore.totalMembershipPaymentsPerYear || []).map(
    (p: any) => ({ year: String(p.year), value: Number(p.totalPayments || 0) })
  );

  const membersPerYearYears = (() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 10 }, (_, i) => now - 9 + i);
  })();
  const membersPerYearMap = new Map(
    (statisticStore.totalMembersPerYear || []).map((it: any) => [Number(it.year), Number(it.totalMembers || 0)])
  );
  const membersPerYearData = membersPerYearYears.map((y) => ({ year: String(y), value: membersPerYearMap.get(y) ?? 0 }));

  const sparkData = (statisticStore.newMembersByMonth || []).map((m: any) => ({ month: m.monthLabel, value: Number(m.count || 0) }));

  const feeGroupOrder = [
    Number(loggedUser?.tenant?.minimumMembershipFee ?? 120),
    Number(loggedUser?.tenant?.individualMembershipFee ?? 240),
    Number(loggedUser?.tenant?.familyMembershipFee ?? 360),
  ];

  const toMonthlyFeeLabelAmount = (annualFee: number) => {
    const monthly = annualFee / 12;
    return Number.isInteger(monthly) ? monthly : Number(monthly.toFixed(2));
  };

  const feeLabels = feeGroupOrder.map((annualFee, index) => {
    const monthlyFee = toMonthlyFeeLabelAmount(annualFee);
    if (index === 0) return t("registrationForm.minimumContribution", { amount: monthlyFee });
    if (index === 1) return t("registrationForm.individualMembership", { amount: monthlyFee });
    return t("registrationForm.familyMembership", { amount: monthlyFee });
  });
  const feeMap = new Map<number, number>(
    (statisticStore.totalMembersByMembershipFee || []).map((x: any) => [
      Number(x.membershipGroupFee),
      Number(x.totalMembers || 0),
    ])
  );
  const feeData = feeGroupOrder.map((grp, i) => ({
    name: feeLabels[i],
    value: feeMap.get(grp) ?? 0,
  }));

  const statusData = memberStatusOptions.map((opt, i) => ({
    name: opt.text,
    value: [statisticStore.totalActiveMembers, statisticStore.totalInactiveMembers, statisticStore.totalPendingMembers, statisticStore.totalArchivedMembers][i] ?? 0,
  }));

  const genderData = salutationTypeOptions.map((opt, i) => ({
    name: opt.text,
    value: [statisticStore.totalMaleMembers, statisticStore.totalFemaleMembers][i] ?? 0,
  }));

  const maxForPayments = Math.max(...membershipPaymentsData.map((d) => d.value), 0);
  const maxForYears = Math.max(...membersPerYearData.map((d) => d.value), 0);
  const maxForSpark = Math.max(...sparkData.map((d) => d.value), 0);

  function makePieValueLabel(colors: string[]) {
    return function (props: any) {
      const { cx, cy, midAngle, innerRadius, outerRadius, percent, index, payload } = props;
      const value = payload?.value ?? 0;
      if (!value || value === 0) return null;
      if ((percent || 0) < 0.04) return null;
      const RAD = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RAD);
      const y = cy + radius * Math.sin(-midAngle * RAD);

      return (
        <text
          x={x}
          y={y}
          fill={"#fff"}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ fontSize: chartFont, fontWeight: 700 }}
        >
          {String(value)}
        </text>
      );
    };
  }

  function legendPositionForData(data: { name: string }[]) {
    const maxLen = Math.max(...data.map(d => (d.name || "").length), 0);
    return maxLen > 18 ? "bottom" : "right";
  }

  function legendPayloadForData(data: { name: string; value: number }[], colors: string[]) {
    return data.map((d, i) => ({
      value: `${d.name} (${d.value})`,
      type: "square",
      id: `${d.name}-${i}`,
      color: (colors && colors[i % colors.length]) || "#000",
    }));
  }

  return (
    <Container sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalActiveMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.activeMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalInactiveMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.inactiveMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            onClick={() => navigate("/pending-members")}
            sx={{ cursor: "pointer" }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalPendingMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.pendingMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalArchivedMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.archivedMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalMembersIncludingFamily}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.totalMembersIncludingFamily")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalMaleMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.maleMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {statisticStore.totalFemaleMembers}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.femaleMembers")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} >
          <Card>
            <CardContent sx={{ textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontSize: kpiValueFont }}>
                {typeof statisticStore.newMembersLast12Months === "number"
                  ? statisticStore.newMembersLast12Months 
                  : 0}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: kpiLabelFont }}>
                {t("dashboard.newMembersLast12Months")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ minHeight: 260, p: 2, mb: 2 }}>
            <Typography variant="h6" align="center" sx={{ mb: 1 }}>
              {t("dashboard.totalMembershipPaymentsPerYear")}
            </Typography>
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={membershipPaymentsData} margin={{ top: 8, right: 12, left: 0, bottom: 6 }}>
                  <XAxis dataKey="year" tick={{ fontSize: tickFont }} />
                  <YAxis tick={{ fontSize: tickFont }} />
                  <ReTooltip formatter={(v: any) => [formatNumber(v)]} />
                  <Bar dataKey="value" fill={COLORS.primary} radius={[6, 6, 0, 0]}>
                    <LabelList
                      dataKey="value"
                      position="top"
                      formatter={(v: any) => (v > 0 && shouldShowBarLabel(v, maxForPayments) ? formatNumber(v) : "")}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Paper>
          <Paper elevation={2} sx={{ minHeight: 220, p: 2, mb: 2 }}>
            <Typography variant="h6" align="center" sx={{ mb: 1 }}>
              {t("dashboard.membersByYear")}
            </Typography>
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={membersPerYearData} margin={{ top: 10, right: 8, left: 0, bottom: 6 }}>
                  <XAxis dataKey="year" tick={{ fontSize: tickFont }} />
                  <YAxis tick={{ fontSize: tickFont }} allowDecimals={false} />
                  <ReTooltip formatter={(v: any) => [formatNumber(v)]} />
                  <Bar dataKey="value" fill={COLORS.membersYear} radius={[6, 6, 0, 0]}>
                    <LabelList
                      dataKey="value"
                      position="top"
                      formatter={(v: any) => (v > 0 && shouldShowBarLabel(v, maxForYears) ? String(v) : "")}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ minHeight: 220, p: 2, mb: 2 }}>
            <Typography variant="h6" align="center" sx={{ mb: 1 }}>
              {t("dashboard.membersByStatus")}
            </Typography>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    paddingAngle={2}
                    label={makePieValueLabel(COLORS.status)}
                    labelLine={false}
                  >
                    {statusData.map((entry, idx) =>
                      entry.value > 0 ? <Cell key={`c-${idx}`} fill={COLORS.status[idx % COLORS.status.length]} /> : <Cell key={`c-${idx}`} fill="#eee" />
                    )}
                  </Pie>
                  <ReTooltip formatter={(v: any) => formatNumber(v)} />
                  {(function () {
                    const pos = breakpoint === "mobile" ? "bottom" : legendPositionForData(statusData);
                    const payload = legendPayloadForData(statusData, COLORS.status);
                    return pos === "right" ? <ReLegend payload={payload} layout="vertical" verticalAlign="middle" align="right" /> : <ReLegend payload={payload} layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ marginTop: 6 }} />;
                  })()}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Paper>
          <Paper elevation={2} sx={{ minHeight: 220, p: 2, mb: 2 }}>
            <Typography variant="h6" align="center" sx={{ mb: 1 }}>
              {t("dashboard.membersByGender")}
            </Typography>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ResponsiveContainer width="100%" height={chartHeight}>
                <PieChart>
                  <Pie
                    data={genderData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    paddingAngle={2}
                    label={makePieValueLabel(COLORS.gender)}
                    labelLine={false}
                  >
                    {genderData.map((entry, idx) =>
                      entry.value > 0 ? <Cell key={`g-${idx}`} fill={COLORS.gender[idx % COLORS.gender.length]} /> : <Cell key={`g-${idx}`} fill="#eee" />
                    )}
                  </Pie>
                  {/* Restore default tooltip for pie charts */}
                  <ReTooltip formatter={(v: any) => formatNumber(v)} />
                  {(function () {
                    const pos = breakpoint === "mobile" ? "bottom" : legendPositionForData(genderData);
                    const payload = legendPayloadForData(genderData, COLORS.gender);
                    return pos === "right" ? <ReLegend payload={payload} layout="vertical" verticalAlign="middle" align="right" /> : <ReLegend payload={payload} layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ marginTop: 6 }} />;
                  })()}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" align="center" sx={{ mb: 1 }}>
              {t("dashboard.membersByMembershipfeeGroup")}
            </Typography>
            <div style={{ height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={feeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    paddingAngle={1}
                    label={makePieValueLabel(COLORS.fee)}
                    labelLine={false}
                  >
                    {feeData.map((entry, idx) =>
                      entry.value > 0 ? (
                        <Cell
                          key={`f-${idx}`}
                          fill={COLORS.fee[idx % COLORS.fee.length]}
                        />
                      ) : (
                        <Cell key={`f-${idx}`} fill="#eee" />
                      )
                    )}
                  </Pie>
                  <ReTooltip formatter={(v: any) => formatNumber(v)} />
                  {(function () {
                    const pos =
                      breakpoint === "mobile"
                        ? "bottom"
                        : legendPositionForData(feeData);
                    const payload = legendPayloadForData(
                      feeData,
                      COLORS.fee
                    );
                    return pos === "right" ? (
                      <ReLegend
                        payload={payload}
                        layout="vertical"
                        verticalAlign="middle"
                        align="right"
                      />
                    ) : (
                      <ReLegend
                        payload={payload}
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        wrapperStyle={{ marginTop: 6 }}
                      />
                    );
                  })()}
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={2} sx={{ p: 2 }}>
            {sparkData.length > 0 && (
              <>
                <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
                  {t("dashboard.newMembersLast12Months") || "New members (last 12 months)"}
                </Typography>
                <CardContent style={{ height: chartHeight }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sparkData} margin={{ top: 8, right: 8, left: 0, bottom: 6 }}>
                      <XAxis
                        dataKey="month"
                        paddingAngle={2}
                        tickLine={false}
                        tick={{ fontSize: tickFont }}
                        interval="preserveStartEnd"
                        tickFormatter={(m: string) => {
                          const parts = (m || "").split(".");
                          return parts.length === 2 ? `${parts[0]}/${parts[1].slice(-2)}` : m;
                        }}
                      />
                      <YAxis tick={{ fontSize: tickFont }} allowDecimals={false} />
                      <ReTooltip formatter={(v: any) => [formatNumber(v)]} />
                      <Bar dataKey="value" fill={COLORS.spark} radius={[6, 6, 0, 0]}>
                        <LabelList
                          dataKey="value"
                          position="top"
                          formatter={(v: any) => (v > 0 && shouldShowBarLabel(v, maxForSpark) ? String(v) : "")}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};
